import { DatabaseTwoTone, MoreOutlined } from "@ant-design/icons";
import {
	IconBrandAndroid,
	IconBrandApple,
	IconBuilding,
	IconCloudFilled,
	IconDeviceDesktop,
	IconDeviceDesktopFilled,
	IconDeviceTablet,
	IconHomeFilled,
	IconLayoutBoardSplit,
	IconPointFilled,
	IconRocket,
	IconSettings2,
	IconSourceCode
} from "@tabler/icons-react";
import type { TransferItem } from "antd/lib/transfer";
import type { DataNode } from "rc-tree/lib/interface";
import type { Key } from "react";
import * as React from "react";
import type { JSX } from "react";
import { type Device, type Node as GqlNode, NodeType } from "src/generated/graphql";

export function mergeAppendById<T>(prev: T[], incoming: T[], getKey: (t: T) => string) {
	const next = [...prev];
	const index = new Map<string, number>();
	next.forEach((r, i) => {
		index.set(getKey(r), i);
	});

	for (const r of incoming) {
		const k = getKey(r);
		const i = index.get(k);
		if (i === undefined) {
			index.set(k, next.length);
			next.push(r);
		} else {
			next[i] = r;
		}
	}

	return next;
}

export const getNodeTypeIcon = (
	node: GqlNode,
	location?: string,
	nodeSubType?: string | undefined,
	isPlaceholder?: boolean
): JSX.Element => {
	if (isPlaceholder) {
		return <img alt="" width="13px" />;
	}

	switch (node.nodeType) {
		case NodeType.Partner:
			return <IconCloudFilled stroke={1.5} color="#2c83b0" width="16" height="16" style={{ marginTop: 0 }} />;

		case NodeType.Channelpartner:
			return <IconBuilding stroke={1.5} color="#2c83b0" width="16" height="16" style={{ marginTop: 0 }} />;

		case NodeType.Company:
			return <IconHomeFilled stroke={1.5} color="#2c83b0" width="16" height="16" style={{ marginTop: 0 }} />;

		case NodeType.Structural:
			return (
				<IconPointFilled stroke={1.5} color="#2c83b0" width="16" height="16" style={{ verticalAlign: "middle" }} />
			);

		case NodeType.Synthetic:
			return getSyntheticNodeTypeIcon(nodeSubType);

		case NodeType.Devices:
			return getDeviceNodeTypeIcon(node);

		default:
			if (location === "DROPDOWN") {
				return (
					<img alt="" width="13px" />
				); /*empty image as a placeholder, 13px = 1em (default size of the ant-design/icons size) */
			} else {
				return <></>;
			}
	}
};

export function getDescendantIds(id: Key, pool: GqlNode[]): Key[] {
	const q: Key[] = [id];
	const out: Key[] = [];
	while (q.length) {
		const cur = q.shift()!;
		const kids = pool.filter(n => n.parentId === cur).map(n => n.id);
		out.push(...(kids as Key[]));
		q.push(...(kids as Key[]));
	}
	return out;
}

const getSyntheticNodeTypeIcon = (nodeSubType?: string): React.JSX.Element => {
	switch (nodeSubType) {
		case "Configurations":
			return <IconSettings2 stroke={1.5} color="darkgrey" width="16" height="16" style={{ marginTop: 0 }} />;
		case "Devices":
			return <IconDeviceDesktop stroke={1.5} color="darkgrey" width="16" height="16" style={{ marginTop: 0 }} />;
		case "Forwarders":
			return <IconRocket stroke={1.5} color="darkgrey" width="16" height="16" style={{ marginTop: 0 }} />;
		case "Server Actions":
			return <IconSourceCode stroke={1.5} color="darkgrey" width="16" height="16" style={{ marginTop: 0 }} />;
		default:
			return <DatabaseTwoTone twoToneColor="#fbba02" style={{ marginTop: 0 }} />;
	}
};

export const lookupNodeTypeIcon = (nodeType: NodeType, location?: string): JSX.Element => {
	const node: GqlNode = { nodeType: nodeType };
	const icon = getNodeTypeIcon(node, location);
	return icon;
};

export const getDeviceNodeTypeIcon2 = (device: Device, width: number = 16, height: number = 16): JSX.Element => {
	let deviceFamily = "";
	if (device.serialNumber?.includes("APPLE")) {
		deviceFamily = "APPLE";
	} else if (device.serialNumber?.includes("ANDROID")) {
		deviceFamily = "ANDROID";
	} else if (device.deviceType?.includes("SMT575")) {
		deviceFamily = "SMT575";
	} else if (device.deviceType?.includes("ADV")) {
		deviceFamily = "ADV";
	}

	let statusColor: any;
	if (device.isEnabled) {
		statusColor = getDeviceStatusColor(
			device.isOnlineRS21 ?? false,
			device.isOnlineWeb ?? false,
			device.isOnlineIOT ?? false,
			deviceFamily
		);
	} else {
		statusColor = "darkgrey";
	}
	// 3) Return the correct icon with the status color
	if (deviceFamily === "APPLE") {
		return <IconBrandApple color={statusColor} width={width} height={height} style={{ marginTop: 0 }} />;
	} else if (deviceFamily === "ANDROID") {
		return <IconBrandAndroid color={statusColor} width={width} height={height} style={{ marginTop: 0 }} />;
	} else if (deviceFamily === "SMT575") {
		return <IconDeviceTablet color={statusColor} width={width} height={height} style={{ marginTop: 0 }} />;
	} else if (deviceFamily === "ADV") {
		return <IconLayoutBoardSplit color={statusColor} width={width} height={height} style={{ marginTop: 0 }} />;
	} else {
		// If not Apple/Android, do your desktop icon, also colorized
		return (
			<IconDeviceDesktop
				color={statusColor}
				width={width}
				height={height}
				style={{
					marginTop: 0
				}}
			/>
		);
	}
};

export const getDeviceNodeTypeIcon = (node: GqlNode): JSX.Element => {
	// 1) Figure out "deviceFamily"
	//    (E.g., your logic might be checking `node.deviceFamily`, or
	//     maybe you see if node.deviceSerialNumber includes 'APPLE' or 'ANDROID'.)
	let deviceFamily = "";
	if (node.deviceSerialNumber?.includes("APPLE")) {
		deviceFamily = "APPLE";
	} else if (node.deviceSerialNumber?.includes("ANDROID")) {
		deviceFamily = "ANDROID";
	} else if (node.deviceType?.includes("SMT575")) {
		deviceFamily = "SMT575";
	} else if (node.deviceType?.includes("ADV")) {
		deviceFamily = "ADV";
	}

	// 2) Compute the overall status color
	//    (If a field is missing, default to false.)
	let statusColor: any;
	if (node.deviceEnabled) {
		statusColor = getDeviceStatusColor(
			node.isOnlineRS21 ?? false,
			node.isOnlineWeb ?? false,
			node.isOnlineIOT ?? false,
			deviceFamily
		);
	} else {
		statusColor = "darkgrey";
	}
	// 3) Return the correct icon with the status color
	if (deviceFamily === "APPLE") {
		return <IconBrandApple color={statusColor} width="16" height="16" style={{ marginTop: 0 }} />;
	} else if (deviceFamily === "ANDROID") {
		return <IconBrandAndroid color={statusColor} width="16" height="16" style={{ marginTop: 0 }} />;
	} else if (deviceFamily === "SMT575") {
		return <IconDeviceTablet color={statusColor} width="16" height="16" style={{ marginTop: 0 }} />;
	} else if (deviceFamily === "ADV") {
		return <IconLayoutBoardSplit color={statusColor} width="16" height="16" style={{ marginTop: 0 }} />;
	} else {
		// If not Apple/Android, do your desktop icon, also colorized
		return (
			<IconDeviceDesktopFilled
				color={statusColor}
				width="16"
				height="16"
				style={{
					marginTop: 0,
					filter:
						"drop-shadow(1px 0 0 #ffffff8e) drop-shadow(-1px 0 0 #ffffff8e) drop-shadow(0 1px 0 #ffffff8e) drop-shadow(0 -1px 0 #ffffff8e)"
				}}
			/>
		);
	}
};

/**
 * This is generic function that returns object property name as string.
 * This function prevents invalid values DURING COMPILE TIME.
 * ES lint will display errors if object were changed.
 * @param name name of the object property
 * @returns object property name as string
 */
export function nameOf<T>(name: keyof T): string {
	const property: keyof T = name;
	return property.toString();
}

export function getProperty<T, K extends keyof T>(o: T, propertyName: K): T[K] {
	return o[propertyName]; // o[propertyName] is of type T[K]
}

export const capitalize = (str: string): string => {
	return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const capitalizeFiles = (str: string): string => {
	return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() + (str.toLowerCase() === "firmware" ? "" : "s");
};

export const toTitleCase = (str: string): string => {
	return str.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase());
};

export const nodeTypeLabel = (nodeType?: NodeType): string => {
	switch (nodeType) {
		case NodeType.Partner:
			return "Partner";
		case NodeType.Channelpartner:
			return "Channel Partner";
		case NodeType.Company:
			return "Company";
		case NodeType.Structural:
			return "Structural Node";
		default:
			return "";
	}
};

/**
 * Function used to sort nodes
 * @param n1
 * @param n2
 */
export const sortNodes = (n1: GqlNode, n2: GqlNode): number => {
	if (!n1.name || !n2.name) return 0;
	if (n1.name > n2.name) {
		return 1;
	}
	if (n1.name < n2.name) {
		return -1;
	}
	return 0;
};

/*
 Function used to walk from a child node to the root.
*/
export const traverseUpTree = (startNode: GqlNode, nodes: GqlNode[]): GqlNode[] => {
	const nodesAbove: GqlNode[] = [];
	let currentNode: GqlNode = nodes?.find(x => x.id == startNode.id) as GqlNode;
	nodesAbove.push(currentNode);

	do {
		if (currentNode?.parentId == null) break;
		const newNodeId = currentNode.parentId;
		currentNode = nodes?.find(x => x.id == newNodeId) as GqlNode;
		nodesAbove.push(currentNode);
	} while (true);

	return nodesAbove;
};

/**
 * Function generates a random integer.
 * @param {number} min
 * @param {number}max
 * @return {number}
 */
export const randomInt = (min: number, max: number): number => {
	return Math.floor(Math.random() * (max - min + 1)) + min;
};

/**
 * Function walks up a tree looking for a nodes parent organisational node.
 * @param {GqlNode[]} nodes
 * @param {number} currentNodeId
 * @return {GqlNode | undefined}
 */
export const findParentNodeGenerated = (nodes: GqlNode[], currentNodeId?: number): GqlNode | undefined => {
	let parentNode: GqlNode;

	if (currentNodeId) {
		parentNode = nodes.find((n: GqlNode) => n.id === currentNodeId) || nodes[0];
	} else {
		parentNode = nodes[0];
	}

	do {
		if (parentNode.nodeType !== NodeType.Structural) break;
		const nodeStep = nodes.find((n: GqlNode) => n.id === parentNode.parentId);
		if (!nodeStep) break;
		parentNode = nodeStep;
	} while (true);

	return parentNode;
};

// eslint-disable-next-line
export const enumToArray = (e: any) => {
	const keys = Object.keys(e);
	return keys.map(key => {
		return { value: key, displayName: e[key] };
	});
};

// eslint-disable-next-line
export const projectIntoTransferItem = (data: any[], keyField: string): any[] => {
	return data.map<TransferItem>(x => {
		const title = x["name"] || x["serialNumber"];
		return {
			...x,
			title: title,
			key: x[keyField]
		};
	});
};

export const getRootNode = (data: GqlNode[], node: GqlNode): GqlNode => {
	const result = data.filter(x => x.id === node.parentId);
	if (result?.[0]) {
		return getRootNode(data, result[0]);
	}
	return node;
};

export const toSentence = (text: string): string => {
	const result = text.replace(/([A-Z])/g, " $1");
	const finalResult = result.charAt(0).toUpperCase() + result.slice(1);
	return finalResult;
};

export const formatBytes = (bytes: number, decimals = 2): string => {
	if (bytes === 0) return "0 Bytes";

	const k = 1024;
	const dm = decimals < 0 ? 0 : decimals;
	const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

	const i = Math.floor(Math.log(bytes) / Math.log(k));

	return parseFloat((bytes / k ** i).toFixed(dm)) + " " + sizes[i];
};

export const createChunks = (file: File, cSize?: number): Blob[] => {
	const chunkSize = cSize ?? 1024 * 1024 * 2;
	let startPointer = 0;
	const endPointer = file.size;
	const chunks = [];
	while (startPointer < endPointer) {
		const newStartPointer = startPointer + chunkSize;
		chunks.push(file.slice(startPointer, newStartPointer));
		startPointer = newStartPointer;
	}
	return chunks;
};

export const string2Bin = (str: string): string[] => {
	const result = [];
	for (let i = 0; i < str.length; i++) {
		result.push(str.charCodeAt(i).toString(2));
	}
	return result;
};

export const isJsonString = (str: string): boolean => {
	try {
		JSON.parse(str);
	} catch (e) {
		return false;
	}
	return true;
};

/**
 * Self-clearing modal-close trigger. Caller passes the close-and-reset callback;
 * the hook owns the trigger state and the firing effect, so consumers don't need
 * their own `useEffect` listening for the boolean to flip.
 *
 * Caller pattern:
 *   const setForceModalClose = useForceModalClose(() => {
 *     setOpen(false);
 *     setSelected(undefined);
 *   });
 *   // ...later: setForceModalClose(true);  // fires the callback once, then auto-resets
 */
export const useForceModalClose = (
	onClose: () => void
): React.Dispatch<React.SetStateAction<boolean>> => {
	const [pending, setPending] = React.useState<boolean>(false);
	const callbackRef = React.useRef(onClose);
	callbackRef.current = onClose;

	React.useEffect(() => {
		if (!pending) return;
		callbackRef.current();
		setPending(false);
	}, [pending]);

	return setPending;
};

export const addIconsToNodes = (data?: GqlNode[] | null): GqlNode[] => {
	if (!data) return [];
	return data.map(item => ({ icon: getNodeTypeIcon(item), ...item }));
};

export const makeId = (): string => {
	let ID = "";
	const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
	for (let i = 0; i < 12; i++) {
		ID += characters.charAt(Math.floor(Math.random() * 36));
	}
	return ID;
};

/**
 * Function will assess if the state should be selected according to optional selected item.
 * @param item data item to be checked
 * @param selectedItem optional selected item
 */
const getSelectedState = (item: GqlNode, selectedItem?: GqlNode) => {
	if (selectedItem) return item.id === selectedItem.id;
	return false;
};

const buildTreeNodeTitle = (item: GqlNode): JSX.Element => {
	if (item.nodeType === NodeType.Synthetic && item.metaNodeType === "DEVICES" && item.deviceCount) {
		return <span>{item.name}</span>;
	}
	return <span>{item.name}</span>;
};

export const arraysAreEqual = (arr1: any[], arr2: any[]): boolean => {
	if (arr1.length !== arr2.length) return false;
	return arr1.every((val, index) => val === arr2[index]);
};

/**
 * Transforms a flat array of nodes into a hierarchical tree structure for use with Tree components.
 * Nodes are sorted with Synthetic and Devices nodes appearing first, followed by other types.
 *
 * @param data - Array of Node objects to transform
 * @param idField - Key of the node that contains its unique identifier
 * @param foreignKey - Key of the node that contains the parent's identifier
 * @param disabled - Whether all nodes should be disabled in the tree
 * @param selectedItem - Optional node to mark as selected in the tree
 * @returns An array of DataNode objects representing the top-level nodes of the tree
 */
export const processFlatDataToTreeData = <T extends GqlNode>(
	data: T[],
	idField: keyof T,
	foreignKey: keyof T,
	disabled: boolean,
	selectedItem?: GqlNode
): DataNode[] => {
	// Filter out nodes without an id
	const filteredData = data.filter(x => x.id);

	// Sort nodes by type priority and name
	filteredData.sort((a, b) => {
		const nameA = a.name ?? "";
		const nameB = b.name ?? "";

		// Give Synthetic and Devices nodes higher priority (appear first)
		const getPriority = (nodeType: NodeType) =>
			nodeType === NodeType.Synthetic || nodeType === NodeType.Devices ? 0 : 1;

		const nodeTypeComparison = getPriority(a.nodeType as NodeType) - getPriority(b.nodeType as NodeType);

		// If node types have the same priority, sort alphabetically
		return nodeTypeComparison || nameA.localeCompare(nameB);
	});

	// Create a map to store nodes by their IDs for efficient lookup
	const nodeMap = new Map<unknown, DataNode>();

	// First pass: Create DataNode objects for all nodes
	filteredData.forEach(item => {
		const placeholder = isLoadMorePlaceholder(item);
		const isSyntheticDevices = item.nodeType === NodeType.Synthetic && item.metaNodeType === "DEVICES";

		// Determine if this node should be displayed as a leaf node
		let isLeafValue = item.parentId == null ? false : !item.hasChildren;
		if (isSyntheticDevices) isLeafValue = false;

		// Create the DataNode with all required properties
		const dataNode: DataNode = {
			...item,
			key: item.id!,
			value: item.id!,
			title: buildTreeNodeTitle(item),
			isLeaf: placeholder ? true : isLeafValue,
			disabled: placeholder || disabled,
			selectable: !placeholder,
			isLoadMorePlaceholder: placeholder,
			selected: item.id === selectedItem?.id,
			icon: getNodeTypeIcon(item, "", item.name ?? "", placeholder),
			switcherIcon: undefined,
			children: []
		};

		nodeMap.set(item[idField], dataNode);
	});

	// Second pass: Build parent-child relationships
	nodeMap.forEach(node => {
		// Get the parent ID, safely typed with the foreignKey
		const parentId = (node as any)[foreignKey];

		if (parentId && nodeMap.has(parentId)) {
			const parentNode = nodeMap.get(parentId)!;
			parentNode.children!.push(node);
			parentNode.isLeaf = false;
		}
	});

	// Identify and collect top-level nodes (nodes without a parent or with a parent not in the dataset)
	const topLevelNodes: DataNode[] = [];
	nodeMap.forEach(node => {
		const parentId = (node as any)[foreignKey];
		if (!parentId || !nodeMap.has(parentId)) {
			topLevelNodes.push(node);
		}
	});

	return topLevelNodes;
};

/**
 * Transforms a flat array of company nodes into a hierarchical tree structure.
 * This is optimized for company data, prioritizing synthetic nodes to appear first.
 *
 * @param data - Array of Node objects representing company data
 * @param idField - Property name that contains the node's unique identifier
 * @param foreignKey - Property name that contains the parent's identifier
 * @param disabled - Whether all nodes should be disabled in the tree
 * @param selectedItem - Optional node to mark as selected
 * @returns An array of DataNode objects representing the root nodes of the company tree
 */
export const processFlatDataToCompanyTreeData = <T extends GqlNode>(
	data: T[],
	idField: keyof T,
	foreignKey: keyof T,
	disabled: boolean,
	selectedItem?: T
): DataNode[] => {
	// Filter nodes without IDs and sort them by priority and name
	const sortedData = data
		.filter(node => node.id != null)
		.sort((a, b) => {
			// Sort first by node type (Synthetic nodes come first)
			const nodeTypeWeight = (node: T): number => (node.nodeType === NodeType.Synthetic ? 0 : 1);

			const typeComparison = nodeTypeWeight(a) - nodeTypeWeight(b);

			// If node types are the same, sort alphabetically by name
			if (typeComparison !== 0) return typeComparison;

			const nameA = a.name ?? "";
			const nameB = b.name ?? "";
			return nameA.localeCompare(nameB);
		});

	// Map nodes to DataNode format with all required properties
	const treeNodes = sortedData.map<DataNode>(node => {
		const nodeType = node.nodeType ?? NodeType.Structural;
		// Ensure key and value are always defined and of type string or number
		const key = node.id ?? `unknown-${Math.random()}`;
		const value = node.id ?? `unknown-${Math.random()}`;

		return {
			...node,
			nodeType, // Preserve the node type for internal use
			key,
			value,
			title: node.name,
			isLeaf: !node.hasChildren,
			disabled,
			selected: getSelectedState(node, selectedItem),
			icon: getNodeTypeIcon(node),
			// Special switcher icon for synthetic nodes
			switcherIcon: nodeType === NodeType.Synthetic ? <MoreOutlined /> : undefined,
			children: []
		};
	});

	// Create a lookup map for faster node retrieval by ID
	const nodeMap = new Map<unknown, DataNode>();
	treeNodes.forEach(node => nodeMap.set(node.key, node));

	// Build parent-child relationships
	treeNodes.forEach(node => {
		const parentId = (node as any)[foreignKey];
		if (parentId != null) {
			const parentNode = nodeMap.get(parentId);
			if (parentNode) {
				// Add this node as a child of its parent
				parentNode.children!.push(node);
				// Since it has at least one child, parent is not a leaf
				parentNode.isLeaf = false;
			}
		}
	});

	// Return only root nodes (those without parents or with parents not in the dataset)
	return treeNodes.filter(node => {
		const parentId = (node as any)[foreignKey];
		return parentId == null || !nodeMap.has(parentId);
	});
};

export const displayCredentialTypeName = (credentialType: string, defaultText: string): string => {
	if (credentialType === "LegacyTemplate") {
		return "Linux Finger Template";
	} else if (credentialType === "Face") {
		return "Android Face Template";
	} else if (credentialType === "Finger") {
		return "Android Finger Template";
	} else if (credentialType === "Photo") {
		return "Profile Photo";
	} else {
		return defaultText;
	}
};

export type ChildrenProps = {
	children?: React.ReactNode;
};

export type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>;

const RELOAD_ONCE_KEY = "__lazy_chunk_reload_once__";

const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

function isChunkImportError(err: unknown): boolean {
	const msg = String((err as any)?.message ?? err ?? "");
	return /Failed to fetch dynamically imported module|ChunkLoadError|Loading chunk|Importing a module script failed|ERR_MODULE_NOT_FOUND|script error/i.test(
		msg
	);
}

/**
 * Use as: React.lazy(() => lazyRetry(() => import('./home'), 'HomePage'))
 */
export function lazyRetry<T extends React.ComponentType<any>>(
	importer: () => Promise<{ default: T }>,
	name?: string,
	opts?: { retries?: number; backoffMs?: number }
): Promise<{ default: T }> {
	const retries = opts?.retries ?? 2;
	const backoffMs = opts?.backoffMs ?? 400;
	let attempt = 0;

	const tryOnce = async (): Promise<{ default: T }> => {
		try {
			attempt++;
			const mod = await importer();
			// success → clear sentinel
			sessionStorage.setItem(RELOAD_ONCE_KEY, "0");
			return mod;
		} catch (err) {
			(window as any).__RELOAD_DEBUG__?.log?.("custom", { source: "lazyRetry", name, attempt, err });

			const chunky = isChunkImportError(err);
			const lastTry = attempt > retries;

			if (import.meta.env.DEV) {
				if (lastTry || !chunky) {
					console.error("[lazyRetry] Dev: failed to load", name ?? "(unnamed)", err);
					const Fallback: React.FC = () => (
						<div style={{ padding: 16 }}>
							<strong>⚠️ Failed to load {name ?? "a part of the app"}.</strong>
							<div>See console for error details. This appears only in dev mode.</div>
						</div>
					);
					return { default: Fallback as unknown as T };
				}
				await delay(backoffMs * attempt);
				return tryOnce();
			}

			// PROD: if it looks like a stale chunk, do one guarded reload; otherwise fail fast
			if (chunky) {
				const already = sessionStorage.getItem(RELOAD_ONCE_KEY) === "1";
				if (!already) {
					sessionStorage.setItem(RELOAD_ONCE_KEY, "1");
					(window as any).__RELOAD_DEBUG__?.log?.("custom", { source: "lazyRetry", action: "prod-reload-once", name });
					window.location.reload();
				}
			}

			if (!lastTry) {
				await delay(backoffMs * attempt);
				return tryOnce();
			}

			throw err instanceof Error ? err : new Error(String(err));
		}
	};

	return tryOnce();
}

export function getDeviceStatusColor(
	isOnlineRS21: boolean,
	isOnlineWeb: boolean,
	isOnlineIOT: boolean,
	deviceFamily?: string
) {
	// Build up the service status array
	// (For non-APPLE/ANDROID devices, include IOT check)

	switch (deviceFamily) {
		case "APPLE":
			return isOnlineRS21 && isOnlineWeb ? "green" : "red";
		case "ANDROID":
			return isOnlineRS21 && isOnlineWeb ? "green" : "red";
		case "SMT575":
			return isOnlineRS21 && isOnlineWeb ? "green" : "red";
		default: {
			const web = isOnlineWeb ? 1 : 0;
			const rs21 = isOnlineRS21 ? 1 : 0;
			const iot = isOnlineIOT ? 1 : 0;

			const sum = web + rs21 + iot;

			if (sum === 3) {
				return "green";
			} else if (sum === 2 || sum === 1) {
				return "orange";
			}

			return "red";
		}
	}
}

export function hasNonZeroData(data: { x: string; y: number }[]) {
	return data.some(point => point.y > 0);
}

export const tickValuesCalc = (data: any, selector: (x: any) => number) => {
	const maxValue = (data ?? []).length > 0 ? Math.max(...(data ?? []).map(selector)) : 0;

	if (maxValue == 1) return [0, 1];

	if (maxValue > 1) {
		const step = Math.ceil(maxValue / 10);
		return Array.from({ length: Math.ceil(maxValue / step) + 1 }, (_, i) => i * step);
	}
	if (maxValue > 0 && maxValue < 10) {
		return Array.from({ length: maxValue }, (_, i) => i + 1);
	}
	if (maxValue > 10 && maxValue < 100) {
		return Array.from({ length: 10 }, (_, i) => i + 1);
	}
	if (maxValue > 100 && maxValue < 1000) {
		return Array.from({ length: 10 }, (_, i) => i * 10 + 10);
	}
	if (maxValue > 1000 && maxValue < 10000) {
		return Array.from({ length: 10 }, (_, i) => i * 100 + 100);
	}

	return Array.from({ length: 10 }, (_, i) => i * 1000 + 1000);
};

export const PLACEHOLDER_REGEX = /^…\d+\s+more devices/i;
export const isLoadMorePlaceholder = (n: any): boolean =>
	n?.isLoadMorePlaceholder === true ||
	(typeof n?.name === "string" && PLACEHOLDER_REGEX.test(n.name)) ||
	(typeof n?.title === "string" && PLACEHOLDER_REGEX.test(n.title));

/** Recursively moves placeholder “… n more devices” rows to the bottom. */
export function movePlaceholderToBottom(items: DataNode[] | undefined): DataNode[] {
	if (!items?.length) return [];

	const devices: DataNode[] = [];
	const placeholders: DataNode[] = [];

	items.forEach(n => (isLoadMorePlaceholder(n) ? placeholders : devices).push(n));

	const dig = (arr: DataNode[]) =>
		arr.map(n => (n.children ? { ...n, children: movePlaceholderToBottom(n.children) } : n));

	return [...dig(devices), ...dig(placeholders)];
}

/** Recursively flags placeholders as un-selectable / disabled. */
export function disablePlaceholders(items: DataNode[]): DataNode[] {
	return items.map(n => {
		const patched = isLoadMorePlaceholder(n)
			? {
					...n,
					selectable: false,
					disabled: true,
					className: `${n.className ?? ""} placeholder-node`
				}
			: n;

		return patched.children ? { ...patched, children: disablePlaceholders(patched.children) } : patched;
	});
}